import { isAbandonedUnpaidClinicalConsultation } from "./clinicalAbandonedUnpaidHold.mjs";

// src/models/consultation/clinicalCreateBlocking.ts
/** Writable statuses queried for create-blocking clinical consultations. */
const CREATE_BLOCKING_CLINICAL_LIFECYCLE_STATUSES = [
	"draft",
	"reserved",
	"ensuring_bridge_service",
	"payment_pending",
	"provisioning",
	"scheduled",
	"pending_cancellation",
	"ensuring_bridge_billing",
	"bridge_settling",
	"bridge_triage",
	"settlement_triage"
];
const INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUSES = [
	"ensuring_bridge_billing",
	"bridge_settling",
	"bridge_triage",
	"settlement_triage"
];
/**
* Lifecycles whose visit should load into the existing-consultation gate when
* opening Book from the hub (Scheduled / Awaiting sign-off / Insurance processing).
* Subset of create-blocking statuses — excludes resume holds and fee-collection rows.
*/
const EXISTING_CONSULTATION_GATE_OCCUPYING_LIFECYCLE_STATUSES = [
	"provisioning",
	"scheduled",
	"pending_cancellation",
	...INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUSES
];
const INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUS_SET = new Set(INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUSES);
const CREATE_BLOCKING_CLINICAL_LIFECYCLE_STATUS_SET = new Set(CREATE_BLOCKING_CLINICAL_LIFECYCLE_STATUSES);
function isCreateBlockingClinicalLifecycleStatus(status) {
	return CREATE_BLOCKING_CLINICAL_LIFECYCLE_STATUS_SET.has(status);
}
const TERMINAL_OPERATIONAL_STATUSES = /* @__PURE__ */ new Set([
	"completed",
	"no_show",
	"booking_cancelled",
	"cancelled"
]);
/**
* Lifecycle statuses that still block create/resume when the consultation is
* operationally active. Terminal operational rows (e.g. lifecycle still
* `scheduled` after visit completion) are stale slot occupants — not blockers.
* Insured settling tail blocks until insurance reconciliation completes unless
* the patient owes an extra settlement amount (`hasUnpaidSettlementCheckout`).
* `pending_cancellation` does not block a new booking, confirmed or not.
* Expired unpaid draft / hold rows do not block when `now` and `createdAt` are set.
* Fee-collection lifecycles (`pending_noshow_fee`, `pending_settlement_payment`)
* do not block — patients may book while manual payment is chased.
*/
function isActiveCreateBlockingClinicalConsultation(consultation) {
	const { lifecycleStatus, operationalStatus } = consultation;
	if (lifecycleStatus === "pending_cancellation") return false;
	if (consultation.createdAt != null && consultation.now != null && isAbandonedUnpaidClinicalConsultation({
		lifecycleStatus,
		createdAt: consultation.createdAt,
		checkoutExpiresAt: consultation.checkoutExpiresAt
	}, consultation.now)) return false;
	if (INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUS_SET.has(lifecycleStatus)) return consultation.hasUnpaidSettlementCheckout !== true;
	if (!isCreateBlockingClinicalLifecycleStatus(lifecycleStatus)) return false;
	return !TERMINAL_OPERATIONAL_STATUSES.has(operationalStatus);
}
/**
* Booking-slot occupancy only — post-scheduled insured tail does not block a new
* booking. Policy attach and sponsorship keep {@link isActiveCreateBlockingClinicalConsultation}.
* A checked-in visit (`scheduled` + `in_progress`) stops blocking once `now >= scheduledStart`.
*/
function isActiveBookingOccupyingClinicalConsultation(consultation) {
	const { lifecycleStatus, operationalStatus } = consultation;
	if (lifecycleStatus === "pending_cancellation") return false;
	if (consultation.createdAt != null && consultation.now != null && isAbandonedUnpaidClinicalConsultation({
		lifecycleStatus,
		createdAt: consultation.createdAt,
		checkoutExpiresAt: consultation.checkoutExpiresAt
	}, consultation.now)) return false;
	if (INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUS_SET.has(lifecycleStatus)) return false;
	if (!isCreateBlockingClinicalLifecycleStatus(lifecycleStatus)) return false;
	if (TERMINAL_OPERATIONAL_STATUSES.has(operationalStatus)) return false;
	if (lifecycleStatus === "scheduled" && operationalStatus === "in_progress" && consultation.scheduledStart != null && consultation.now != null && consultation.now >= consultation.scheduledStart) return false;
	return true;
}
const INSURED_POLICY_CHANGE_LOCK_RESUME_HOLD_STATUSES = /* @__PURE__ */ new Set([
	"reserved",
	"payment_pending",
	"ensuring_bridge_service"
]);
const INSURED_POLICY_CHANGE_LOCK_LIFECYCLE_STATUSES = /* @__PURE__ */ new Set([
	...INSURED_POLICY_CHANGE_LOCK_RESUME_HOLD_STATUSES,
	"provisioning",
	"scheduled",
	...INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUSES,
	"pending_settlement_payment"
]);
/**
* Insured booking: lock policy swap once a slot is reserved, until the visit lifecycle
* completes. Resume holds lock only while still resumable (same abandon/expiry rule as
* create/resume). Settling with an unpaid checkout still locks — tighter than
* {@link isActiveCreateBlockingClinicalConsultation}.
*/
function isInsuredPolicyChangeLockedByConsultation(consultation) {
	if (consultation.billingPath !== "insured") return false;
	const { lifecycleStatus, operationalStatus } = consultation;
	if (!INSURED_POLICY_CHANGE_LOCK_LIFECYCLE_STATUSES.has(lifecycleStatus)) return false;
	if (INSURED_POLICY_CHANGE_LOCK_RESUME_HOLD_STATUSES.has(lifecycleStatus)) {
		const now = consultation.now ?? /* @__PURE__ */ new Date();
		if (isAbandonedUnpaidClinicalConsultation({
			lifecycleStatus,
			createdAt: consultation.createdAt ?? now,
			checkoutExpiresAt: consultation.checkoutExpiresAt
		}, now)) return false;
		return true;
	}
	if (lifecycleStatus === "scheduled" || lifecycleStatus === "provisioning") return !TERMINAL_OPERATIONAL_STATUSES.has(operationalStatus);
	return true;
}

export { CREATE_BLOCKING_CLINICAL_LIFECYCLE_STATUSES, EXISTING_CONSULTATION_GATE_OCCUPYING_LIFECYCLE_STATUSES, INSURED_SETTLING_CREATE_BLOCKING_LIFECYCLE_STATUSES, isActiveBookingOccupyingClinicalConsultation, isActiveCreateBlockingClinicalConsultation, isCreateBlockingClinicalLifecycleStatus, isInsuredPolicyChangeLockedByConsultation };
//# sourceMappingURL=clinicalCreateBlocking.mjs.map